import { Theme } from "@mui/material";
import { YAxisOptions, XAxisOptions, LegendOptions, ChartOptions, XAxisLabelsOptions, YAxisLabelsOptions, YAxisTitleOptions, XAxisTitleOptions, Options, SeriesOptionsType } from "highcharts";
import { adjustColorContrast } from "./AdjustColorContrast";

const targetContrastRatio = 3.0; // WCAG AA minimum for large text

export function themifyHighchartsOptions<TOptions extends Options>(
    options: TOptions,
    theme: Theme
): TOptions {
    return {
        ...options,
        chart: themifyChartOptions(options?.chart, theme),
        legend: themifyLegendOptions(options?.legend, theme),
        xAxis: themifyAxisOptions<XAxisOptions>(options?.xAxis, theme),
        yAxis: themifyAxisOptions<YAxisOptions>(options?.yAxis, theme),
        series: themifySeriesOptions(options.series, theme)
    } as TOptions;
}

export function themifyChartOptions(
    chartOptions: ChartOptions | undefined,
    _theme: Theme
): ChartOptions | undefined {
    return {
        ...chartOptions,
        backgroundColor: "transparent",
        plotBackgroundColor: "transparent",
        style: {
            fontFamily: "inherit"
        }
    };
}

export function themifyLegendOptions(
    legendOptions: LegendOptions | undefined,
    theme: Theme
): LegendOptions | undefined {
    return {
        ...legendOptions,
        itemStyle: {
            color: theme.palette.text.primary,
            fontWeight: "bold"
        }
    } as LegendOptions;
}

export function themifyAxisOptions<TAxisOptions extends YAxisOptions | XAxisOptions>(
    axisOptions: TAxisOptions | Array<TAxisOptions> | undefined,
    theme: Theme
): TAxisOptions | Array<TAxisOptions> | undefined {
    return axisOptions
        ? Array.isArray(axisOptions)
            ? (axisOptions as Array<TAxisOptions>).map(
                axis => themifyAxisOptions(axis, theme)
            ) as Array<TAxisOptions>
            : {
                ...axisOptions,
                labels: themifyAxisLabelsOptions(axisOptions?.labels, theme),
                title: themifyAxisTitleOptions(axisOptions?.title, theme),
                lineColor: theme.palette.text.primary,
                tickColor: theme.palette.text.primary
            } as TAxisOptions
        : undefined;
}

export function themifyAxisLabelsOptions<TAxisLabelsOptions extends XAxisLabelsOptions | YAxisLabelsOptions>(
    labelOptions: TAxisLabelsOptions | undefined,
    theme: Theme
): TAxisLabelsOptions | undefined | null {
    return {
        ...labelOptions,
        style: {
            color: (labelOptions?.style?.color ?? "#000000") === "#000000"
                ? theme.palette.text.primary
                : labelOptions?.style?.color
        }
    } as TAxisLabelsOptions;
}

export function themifyAxisTitleOptions<TAxisTitleOptions extends YAxisTitleOptions | XAxisTitleOptions>(
    titleOptions: TAxisTitleOptions | undefined,
    theme: Theme
): TAxisTitleOptions | undefined {
    return {
        ...titleOptions,
        style: {
            color: (titleOptions?.style?.color ?? "#000000") === "#000000"
                ? theme.palette.text.primary
                : titleOptions?.style?.color
        }
    } as TAxisTitleOptions;
}

export function themifySeriesOptions(
    seriesOptions: Array<SeriesOptionsType> | undefined,
    theme: Theme
) {
    return seriesOptions
        ? seriesOptions.map(options => {
            if (options.hasOwnProperty("color") && typeof (options as any).color === "string") {
                return {
                    ...options,
                    color: adjustColorContrast(
                        (options as any).color,
                        theme.palette.background.paper,
                        targetContrastRatio
                    )
                };
            } else {
                return options;
            }
        })
        : undefined;
}
